"use client";

import React, { useState, useEffect } from "react";
import { ArrowUpRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function EstimateLinks() {
  const [pathname, setPathname] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    details: "",
    contact_method: "",
    timing: "",
    budget: "",
    consent: false
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      setPathname(window.location.pathname || "/");
    }
  }, []);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleConsentChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      consent: checked
    }));
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!formData.consent) {
      setErrorMessage("Please agree to be contacted to submit your request.");
      return;
    }
    if (!formData.name || !formData.phone || !formData.email || !formData.address || !formData.project_type || !formData.contact_method || !formData.timing) {
      setErrorMessage("Please fill in all required fields.");
      return;
    }
    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("phone", formData.phone);
      params.append("email", formData.email);
      params.append("address", formData.address);
      params.append("project_type", formData.project_type);
      params.append("details", formData.details);
      params.append("contact_method", formData.contact_method);
      params.append("timing", formData.timing);
      params.append("budget", formData.budget);
      params.append("consent", formData.consent ? "true" : "false");
      params.append("website", "DecaturHomeRenovation.com");
      params.append("page", pathname);
      params.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const relatedLinks = [{
    title: "Basement Remodeling",
    to: "/basement-remodeling",
    description: "Explore transformed lower levels, custom living spaces, and entertainment suites."
  }, {
    title: "Whole-Home",
    to: "/whole-home",
    description: "Complete interior renovations crafted with cohesive architectural character."
  }, {
    title: "Remodeling Cost Guide",
    to: "/remodeling-cost-guide",
    description: "Transparent budgeting expectations and neighborhood planning insights."
  }];
  return <section id="relatedlinksandestimateform" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Related Links Block */}
        <div className="mb-16">
          <div className="max-w-2xl mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
              Explore Related Services & Guides
            </h2>
            <p className="font-sans text-muted-foreground mt-2 text-base md:text-lg">
              Learn more about our renovation specialties or review realistic cost benchmarks for your home.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedLinks.map((item, i) => <Card key={item.to} data-index={i} className="group relative border border-border/80 bg-card/70 hover:bg-card hover:border-foreground/30 transition-all duration-300 shadow-sm hover:shadow-md rounded-xl p-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-serif text-xl md:text-2xl font-semibold text-foreground group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <div className="size-8 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                      <ArrowUpRight className="size-4" />
                    </div>
                  </div>
                  <p className="font-sans text-muted-foreground text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
                <div className="pt-6">
                  <Button asChild variant="link" className="p-0 h-auto font-sans font-medium text-foreground hover:text-primary group-hover:translate-x-0.5 transition-transform">
                    <Link to={item.to}>
                      View details
                      <span className="sr-only"> for {item.title}</span>
                    </Link>
                  </Button>
                </div>
              </Card>)}
          </div>
        </div>

        {/* Estimate Form Block */}
        <div className="max-w-3xl mx-auto">
          <Card className="border border-border/80 bg-card shadow-md rounded-xl p-6 sm:p-10">
            <div className="text-center mb-8">
              <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
                Request an Estimate
              </h2>
              <p className="font-sans text-muted-foreground mt-2 text-base">
                Tell us about your home and timeline to schedule an on-site consultation.
              </p>
            </div>

            {isSuccess ? <div className="py-12 px-4 text-center flex flex-col items-center">
                <div className="size-14 rounded-full bg-secondary text-primary flex items-center justify-center mb-4">
                  <CheckCircle2 className="size-8 text-primary" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-foreground mb-2">
                  Request Received
                </h3>
                <p className="font-sans text-muted-foreground max-w-md text-base">
                  Thank you for reaching out. We have received your project details and will be in touch shortly through your preferred contact method.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-6">
                {errorMessage && <Alert variant="destructive" className="border-destructive/30">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Notice</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-sans">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input id="name" name="name" type="text" required placeholder="Jane Doe" value={formData.name} onChange={handleInputChange} className="font-sans" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-sans">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input id="phone" name="phone" type="tel" required placeholder="Your phone number" value={formData.phone} onChange={handleInputChange} className="font-sans" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-sans">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input id="email" name="email" type="email" required placeholder="Your email address" value={formData.email} onChange={handleInputChange} className="font-sans" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address" className="font-sans">
                      Street Address or ZIP Code <span className="text-destructive">*</span>
                    </Label>
                    <Input id="address" name="address" type="text" required placeholder="Decatur, GA or 30030" value={formData.address} onChange={handleInputChange} className="font-sans" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="project_type" className="font-sans">
                      Project Type <span className="text-destructive">*</span>
                    </Label>
                    <Select value={formData.project_type} onValueChange={val => handleSelectChange("project_type", val)} required>
                      <SelectTrigger id="project_type" className="w-full font-sans">
                        <SelectValue placeholder="Select project type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Kitchen Remodeling">Kitchen Remodeling</SelectItem>
                        <SelectItem value="Bathroom Remodeling">Bathroom Remodeling</SelectItem>
                        <SelectItem value="Whole-Home Remodeling">Whole-Home Remodeling</SelectItem>
                        <SelectItem value="Home Additions">Home Additions</SelectItem>
                        <SelectItem value="Basement Remodeling">Basement Remodeling</SelectItem>
                        <SelectItem value="Interior Remodeling">Interior Remodeling</SelectItem>
                        <SelectItem value="Outdoor Living">Outdoor Living</SelectItem>
                        <SelectItem value="Deck Remodeling">Deck Remodeling</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact_method" className="font-sans">
                      Preferred Contact Method <span className="text-destructive">*</span>
                    </Label>
                    <Select value={formData.contact_method} onValueChange={val => handleSelectChange("contact_method", val)} required>
                      <SelectTrigger id="contact_method" className="w-full font-sans">
                        <SelectValue placeholder="Select contact method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Phone">Phone</SelectItem>
                        <SelectItem value="Text">Text</SelectItem>
                        <SelectItem value="Email">Email</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="timing" className="font-sans">
                      Project Timing <span className="text-destructive">*</span>
                    </Label>
                    <Select value={formData.timing} onValueChange={val => handleSelectChange("timing", val)} required>
                      <SelectTrigger id="timing" className="w-full font-sans">
                        <SelectValue placeholder="Select timing" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ASAP">ASAP</SelectItem>
                        <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                        <SelectItem value="1-3 months">1-3 months</SelectItem>
                        <SelectItem value="3-6 months">3-6 months</SelectItem>
                        <SelectItem value="Just researching">Just researching</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="budget" className="font-sans">
                      Estimated Budget
                    </Label>
                    <Select value={formData.budget} onValueChange={val => handleSelectChange("budget", val)}>
                      <SelectTrigger id="budget" className="w-full font-sans">
                        <SelectValue placeholder="Select budget range (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Under $25,000">Under $25,000</SelectItem>
                        <SelectItem value="$25,000-$50,000">$25,000-$50,000</SelectItem>
                        <SelectItem value="$50,000-$100,000">$50,000-$100,000</SelectItem>
                        <SelectItem value="$100,000+">$100,000+</SelectItem>
                        <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="details" className="font-sans">
                    Project Details
                  </Label>
                  <Textarea id="details" name="details" rows={4} placeholder="Tell us about the space, goals, or any specific ideas you have in mind..." value={formData.details} onChange={handleInputChange} className="font-sans resize-y" />
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <Checkbox id="consent" checked={formData.consent} onCheckedChange={checked => handleConsentChange(checked === true)} className="mt-1" />
                  <Label htmlFor="consent" className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed cursor-pointer">
                    I agree to be contacted about my project by phone, text, or email.{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                </div>

                <div className="pt-4">
                  <Button type="submit" disabled={isSubmitting} className="w-full font-sans text-base py-6 rounded-md shadow-sm">
                    {isSubmitting ? <>
                        <Loader2 className="size-5 animate-spin" />
                        Sending Request...
                      </> : "Submit Estimate Request"}
                  </Button>
                </div>
              </form>}
          </Card>
        </div>
      </div>
    </section>;
}